/**
 * src/lib/theme/animated-theme-change.ts
 *
 * Magic UI tarzi animasyonlu tema gecisi yardimcisi. View Transitions API
 * destekleyen tarayicilarda, tema degisikligini bir buton orijininden acilan
 * clip-path circle animasyonu ile yapar.
 *
 * Hem public site (ThemeToggle) hem CMS panel (CmsTopBar) ayni helper'i
 * kullanir — kod tekrarini onler, davranisi tek noktadan yonetir.
 *
 * Animation CSS'i: globals.css icindeki @keyframes theme-toggle-clip-expand +
 * ::view-transition-new(root) kurali. CSS variables: --theme-toggle-x / -y
 * (px), buton merkezi koordinatlari.
 *
 * Browser destegi yoksa veya prefers-reduced-motion: reduce ise animasyon
 * atlanir, sadece `apply` fonksiyonu cagirilir (instant switch).
 *
 * Server-only modul DEGIL — sadece browser environment'ta cagrilmalidir
 * ('use client' bilesenlerinden).
 */

import { flushSync } from 'react-dom'

/**
 * `Document.startViewTransition` arayuzu — TS lib.dom henuz tum runtime'larda
 * bu API'yi tanimlamamis olabilir. Sadece `finished` Promise'i ihtiyac
 * duydugumuz alan; minimal type.
 */
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> }
}

/** triggerAnimatedThemeChange parametreleri. */
export interface AnimatedThemeChangeOptions {
  /**
   * Animasyonun acilma noktasini belirleyen element (genellikle tiklanan
   * buton). Yoksa veya ekran disindaysa ekran ortasi kullanilir.
   */
  origin: HTMLElement | null
  /**
   * Yeni tema class adi — `'light'` veya `'dark'` (next-themes default class
   * isimleri). Helper bu class'i `<html>` uzerinde manuel toggle eder, ki
   * View Transition snapshot YENI tema ile cekilsin.
   *
   * Neden manuel? next-themes'in `setTheme`'i React state update yapar ve
   * DOM class manipulation'i bir `useEffect` icinde gerceklesir. `flushSync`
   * `useEffect`'i flush etmedigi icin, startViewTransition snapshot'i ESKI
   * class ile cekilirdi -> iki snapshot ayni -> animasyon gorunmez.
   *
   * Manuel toggle ile DOM update synchronous olur; next-themes daha sonra
   * ayni class'i set eder (no-op).
   */
  nextTheme: 'light' | 'dark'
  /**
   * Tema degisikligini React/next-themes tarafinda kayda gecirir. Genellikle
   * `() => setTheme(next)`. localStorage write + state update icin gerekli.
   * flushSync icinde calistirilir.
   */
  apply: () => void
}

/**
 * Animasyonlu tema gecisi tetikler. View Transitions API destekleniyorsa
 * clip-path circle expand animasyonu ile; aksi halde instant switch.
 *
 * @param options Origin elementi + tema degisikligi callback'i
 *
 * @example
 * ```tsx
 * triggerAnimatedThemeChange({
 *   origin: buttonRef.current,
 *   apply: () => setTheme('dark'),
 * })
 * ```
 */
export function triggerAnimatedThemeChange(options: AnimatedThemeChangeOptions): void {
  const { origin, nextTheme, apply } = options
  const doc = document as ViewTransitionDocument
  const root = document.documentElement

  /**
   * `<html>` uzerinde light/dark class'larini ayarlar. next-themes default
   * class isimleri ('light', 'dark'). startViewTransition snapshot'i bu
   * DOM mutasyondan SONRA cekilsin diye callback icinde cagirilir.
   */
  const applyClassesManually = () => {
    root.classList.remove('light', 'dark')
    root.classList.add(nextTheme)
    // color-scheme native browser scrollbar/form rengi icin de set edilir
    // (next-themes ayrica style.colorScheme set eder; kendimiz de yapinca
    // snapshot dogru rengi yakalar).
    root.style.colorScheme = nextTheme
  }

  // Reduced motion preferi a11y kurali — animasyonu zorla atla.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // Fallback: API yok veya reduced motion -> instant switch.
  if (typeof doc.startViewTransition !== 'function' || prefersReducedMotion) {
    apply()
    return
  }

  // Origin element merkezi -> CSS variables (px). Yoksa ekran ortasi.
  const rect = origin?.getBoundingClientRect()
  const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2
  const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2
  root.style.setProperty('--theme-toggle-x', `${x}px`)
  root.style.setProperty('--theme-toggle-y', `${y}px`)

  // startViewTransition callback:
  //   1. DOM class'i manuel toggle (snapshot YENI tema'yi yakalar)
  //   2. flushSync(apply) — next-themes state + localStorage update senkron
  //   useEffect'in geri donen DOM toggle'i no-op olur (class zaten dogru).
  // 🔴 Donen gecisin `finished` promise'i MUTLAKA yakalanmali.
  //
  // View Transition yarida kesilirse (kullanici gecis surerken sayfa
  // degistirirse ya da ikinci bir gecis baslarsa) bu promise
  // `InvalidStateError: Transition was aborted because of invalid state`
  // ile reddediliyor. Donus degeri atildigi icin bu, yakalanmamis bir promise
  // reddi olarak `window.onunhandledrejection`'a dusuyor ve Sentry'ye hata
  // olarak gidiyordu (SECURESYS-12; /cms/blog/:id/duzenle sayfasinda tema
  // dugmesine basip hemen gezinince olustu, DOMException kod 11).
  //
  // Iptal ZARARSIZ: geri cagirim zaten calisti, tema uygulandi; yalniz
  // animasyon tamamlanmadi. Bu yuzden sessizce yutuluyor — baska bir hata
  // turu gelirse yeniden firlatiliyor ki gercek sorunlar gizlenmesin.
  const gecis = doc.startViewTransition(() => {
    applyClassesManually()
    flushSync(() => {
      apply()
    })
  })

  gecis.finished.catch((err: unknown) => {
    const iptalEdildi =
      err instanceof DOMException && (err.name === 'InvalidStateError' || err.name === 'AbortError')
    if (!iptalEdildi) throw err
  })
}
